import { EntityStatus, getStatusForId } from '@ms/yammer-data-support';
import { CampaignEditableFilter } from '@ms/yammer-graphql';
import createCachedSelector from 're-reselect';

import { Campaign, CampaignReference, CampaignScope, CampaignWithReference, YammerState } from '../../state/types';
import { getFulfilledCampaignCoverImages } from '../campaignCoverImage/selectors';
import { isCampaignsCoOrganizersEnabled } from '../campaignRights/selectors';
import { createFindById } from '../findById';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';

import { campaignsListRequestStatusKey } from './loadMoreAllCampaignsActions.types';
import { AllCampaignsLoadOptions } from './shared.types';

export const findById = createFindById('allCampaigns');

const getCampaignScopeKey = (scope?: CampaignScope) => {
  switch (scope?.type) {
    case 'GROUP':
      return `-GROUP_${scope.groupId}`;
    case 'NETWORK':
      return `-NETWORK`;
    case 'MULTI_TENANT_ORGANIZATION':
      return `-MULTI_TENANT_ORGANIZATION`;
    default:
      return '';
  }
};

const getCampaignEditableFilterKey = (filterByEditable?: CampaignEditableFilter) => {
  switch (filterByEditable) {
    case CampaignEditableFilter.ALL:
      return `-${CampaignEditableFilter.ALL}`;
    case CampaignEditableFilter.VIEWER_CAN_EDIT:
      return `-${CampaignEditableFilter.VIEWER_CAN_EDIT}`;
    default:
      return '';
  }
};

type GetAllCampaignsKey = (options: AllCampaignsLoadOptions) => string;
export const getAllCampaignsKey: GetAllCampaignsKey = ({
  sortBy,
  filterByState,
  scope,
  filterByEditable,
  isOrganizerCheck,
}) =>
  `${sortBy}-${filterByState}${getCampaignScopeKey(scope)}${getCampaignEditableFilterKey(filterByEditable)}${
    isOrganizerCheck ? '-ORGANIZER-CHECK' : ''
  }`;

type GetAllCampaignsStatus = (state: YammerState, options: AllCampaignsLoadOptions) => EntityStatus;
export const getAllCampaignsStatus: GetAllCampaignsStatus = (state, options) =>
  getStatusForId(state.allCampaigns, getAllCampaignsKey(options));

const emptyCampaignIds: Campaign['id'][] = [];
export const getAllCampaignsIdsFromState = (state: YammerState, options: AllCampaignsLoadOptions) =>
  findById(state, getAllCampaignsKey(options))?.campaignIds ?? emptyCampaignIds;

type GetFulfilledCampaigns = (state: YammerState) => Record<string, Campaign>;
export const getFulfilledCampaigns: GetFulfilledCampaigns = (state) => state.campaigns.fulfilled.byId;

type GetFulfilledCampaignReferences = (state: YammerState) => Record<string, CampaignReference>;
export const getFulfilledCampaignReferences: GetFulfilledCampaignReferences = (state) =>
  state.campaignReferences.fulfilled.byId;

type GetCampaigns = (state: YammerState, options: AllCampaignsLoadOptions) => CampaignWithReference[];
export const getCampaigns: GetCampaigns = createCachedSelector(
  getAllCampaignsIdsFromState,
  getFulfilledCampaigns,
  getFulfilledCampaignReferences,
  getFulfilledCampaignCoverImages,
  (allCampaignIds, fulfilledCampaigns, fulfilledCampaignReferences, fulfilledCampaignCoverImages) =>
    allCampaignIds.map((id) => {
      const campaign = fulfilledCampaigns[id];
      if (!campaign) {
        throw new Error('Missing campaign data in state for all campaign edge.');
      }

      return {
        ...fulfilledCampaigns[id],
        ...fulfilledCampaignReferences[id],
        ...fulfilledCampaignCoverImages[id],
      };
    })
)((_state, options) => getAllCampaignsKey(options));

type GetLoadMoreCampaignsListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreCampaignsListStatus: GetLoadMoreCampaignsListStatus = (state) =>
  getRequestStatus(state, 'CampaignsListMore', campaignsListRequestStatusKey);

type HasNextPageOfCampaignsListAvailable = (state: YammerState, options: AllCampaignsLoadOptions) => boolean;
export const hasNextPageOfCampaignsListAvailable: HasNextPageOfCampaignsListAvailable = (state, options) =>
  findById(state, getAllCampaignsKey(options))?.hasNextPage ?? false;

type HasOrganizedCampaigns = (state: YammerState) => boolean;
export const hasOrganizedCampaigns: HasOrganizedCampaigns = (state) => {
  const campaignsCoOrganizersIsEnabled = isCampaignsCoOrganizersEnabled(state);

  return campaignsCoOrganizersIsEnabled && !!state.allCampaigns.hasOrganizedCampaigns;
};
